import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridRowClassNameParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  Stack,
  Link,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { toast } from "react-toastify";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { ForumThread } from "../../types/forum";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  useDeleteForumThreadMutation,
  useGetForumThreadsQuery,
} from "../../services/forum.service";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { Download as DownloadIcon } from "@mui/icons-material";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";

const SocialFeedsPage = () => {
  useTitle("Social Feeds");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [deleteModal, setDeleteModal] = React.useState<{
    open: boolean;
    row: GridRowModel<ForumThread> | null;
  }>({
    open: false,
    row: null,
  });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  const { data, isLoading, isFetching } = useGetForumThreadsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const [doDelete, { isLoading: deleting }] = useDeleteForumThreadMutation();

  const handleDeleteRow = React.useCallback((row: GridRowModel<ForumThread>) => {
    setDeleteModal({ open: true, row });
  }, []);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: "id", width: 50 },
      { field: "title", hide: true },
      {
        field: "user",
        hide: true,
        valueGetter: (params: GridValueGetterParams<undefined, ForumThread>) =>
          params.row.user?.full_name,
      },
      {
        field: "thread",
        headerName: "Thread Title / Poster",
        minWidth: isMobile ? 120 : 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams<undefined, ForumThread>) => (
          <Box>
            <Box>
              <Link
                onClick={() => navigate(`/social/feeds/${params.row.id}`)}
                component="button"
                variant="body2"
                sx={{
                  color: "#F4A259",
                  fontWeight: 600,
                  textDecoration: "none",
                  fontSize: isMobile ? "0.75rem" : "0.875rem",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "#E8944A",
                  },
                }}
              >
                #{params.row.title}
              </Link>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}>
              {params.row.user?.full_name}
            </Typography>
          </Box>
        ),
      },
      // {
      //   field: "info",
      //   headerName: "Views/ Comments/ Likes",
      //   minWidth: 200,
      //   disableExport: true,
      //   renderCell: (params: GridRenderCellParams<undefined, ForumThread>) => (
      //     <Stack direction={"row"} alignItems={"center"}>
      //       <Typography variant="body2">{params.row.total_view}</Typography>{" "}
      //       <VisibilityOutlined fontSize="inherit" htmlColor="#999" />
      //       <Typography variant="body2" style={{ paddingLeft: 10 }}>
      //         {params.row.total_comments}
      //       </Typography>{" "}
      //       <CommentOutlined fontSize="inherit" htmlColor="#999" />
      //       <Typography variant="body2" style={{ paddingLeft: 10 }}>
      //         {params.row.total_likes}
      //       </Typography>{" "}
      //       <ThumbUpAltOutlined fontSize="inherit" htmlColor="#999" />
      //     </Stack>
      //   ),
      // },
      {
        field: "Views",
        headerName: "# of Views",
        minWidth: isMobile ? 80 : 120,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams<undefined, ForumThread>) =>
          params.row.total_view || "-",
      },
      {
        field: "Comments",
        headerName: "# of Comments",
        minWidth: isMobile ? 80 : 120,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams<undefined, ForumThread>) =>
          params.row.total_comments || "-",
      },
      {
        field: "Likes",
        headerName: "# of Likes",
        minWidth: isMobile ? 80 : 120,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams<undefined, ForumThread>) =>
          params.row.total_likes || "-",
      },
      {
        field: "num_of_doc",
        headerName: "Attachments",
        minWidth: isMobile ? 80 : 100,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams<undefined, ForumThread>) =>
          params.row.documents?.length || "-",
      },
      {
        field: "is_locked",
        headerName: "Locked?",
        minWidth: isMobile ? 80 : 100,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams<undefined, ForumThread>) =>
          params.row.is_locked ? "Locked" : "-",
      },
      {
        field: "is_pinned",
        headerName: "Pinned?",
        minWidth: isMobile ? 80 : 100,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams<undefined, ForumThread>) =>
          params.row.is_pinned ? "Pinned" : "-",
      },
      {
        field: "created_at",
        headerName: "Created At",
        minWidth: isMobile ? 120 : 150,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams<undefined, ForumThread>) =>
          `${dayjs(params.row?.created_at).format("MMM DD, hh:mm A")}`,
      },
      {
        field: "control",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        hide: false,
        width: isMobile ? 80 : 120,
        minWidth: isMobile ? 80 : 120,
        maxWidth: isMobile ? 80 : 120,
        flex: 0,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<undefined, ForumThread>) => (
          <IconButton
            size="small"
            onClick={() => handleDeleteRow(params.row)}
            sx={{
              color: "#d32f2f",
              "&:hover": {
                backgroundColor: "rgba(211, 47, 47, 0.1)",
              },
            }}
          >
            <DeleteOutline fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        ),
        disableExport: true,
      },
    ],
    [handleDeleteRow, isMobile]
  );

  const closeDeleteModal = () =>
    setDeleteModal({ ...deleteModal, open: false });

  const handleDelete = async () => {
    if (deleteModal.row?.id) {
      try {
        await doDelete(deleteModal.row.id).unwrap();
        closeDeleteModal();
        toast.success("Successfully deleted");
      } catch (err) {
        closeDeleteModal();
      }
    }
  };

  const handleExport = React.useCallback(() => {
    if (!rows || rows.length === 0) return;

    // Get visible columns (excluding control column)
    const visibleColumns = columns.filter((col) => col.field !== "control" && !col.hide);

    // Create CSV headers
    const headers = visibleColumns.map((col) => `"${col.headerName || col.field}"`).join(",");

    // Create CSV rows
    const csvRows = rows.map((row: any) => {
      return visibleColumns
        .map((col) => {
          let value: string = "";
          if (col.valueGetter) {
            const params = { row } as any;
            const result = col.valueGetter(params);
            value = result != null ? String(result) : "";
          } else if (col.field) {
            const rawValue = (row as any)[col.field];
            value = rawValue ? String(rawValue) : "";
          }
          // Escape quotes and wrap in quotes if needed
          if (value && (value.includes(",") || value.includes('"') || value.includes("\n"))) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
          return value || "";
        })
        .join(",");
    });

    // Combine header and rows
    const csvContent = [headers, ...csvRows].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Social_Feeds_${dayjs().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows, columns]);

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  return (
    <>
      <CardBase
        title="Social Feeds"
        useGlassCard={false}
        sx={{ height: isMobile ? "70vh" : "85vh", p: 0 }}
        headerActions={
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={1}
            alignItems={isMobile ? "stretch" : "center"}
            sx={{ width: isMobile ? "100%" : "auto" }}
          >
            <StyledTextField
              size="small"
              label="Enter to search.."
              value={query || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              sx={{ minWidth: isMobile ? "100%" : 250 }}
            />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                width: isMobile ? "100%" : "auto",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}
            >
              {!!query && (
                <Button
                  variant="text"
                  onClick={() => setQuery("")}
                  size="small"
                  fullWidth={isMobile}
                  sx={{
                    color: "#F4A259",
                    "&:hover": {
                      backgroundColor: "rgba(244, 162, 89, 0.08)",
                    },
                  }}
                >
                  Reset
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={!rows || rows.length === 0}
                size="small"
                fullWidth={isMobile}
                sx={{
                  color: "#F4A259",
                  borderColor: "#F4A259",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#F4A259",
                    backgroundColor: "rgba(244, 162, 89, 0.08)",
                    transform: "translateY(-1px)",
                  },
                  "&:disabled": {
                    borderColor: "rgba(244, 162, 89, 0.5)",
                    color: "rgba(244, 162, 89, 0.5)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {isMobile ? "Export" : "Export CSV"}
              </Button>
            </Stack>
          </Stack>
        }
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          rowHeight={64}
          density="compact"
          height={isMobile ? "calc(70vh - 80px)" : "calc(85vh - 80px)"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              paddingLeft: isMobile ? 1.5 : 3,
              paddingRight: isMobile ? 1.5 : 3,
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: isMobile ? "0.75rem" : "0.875rem",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: isMobile ? 1.5 : 3,
              paddingRight: isMobile ? 1.5 : 3,
              py: isMobile ? 1 : 2,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.05)",
            },
            "& .deleted": {
              fontStyle: "italic",
              color: "#aaa",
            },
          }}
          getRowClassName={getRowClassName}
        />
      </CardBase>
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        message={`Delete Feed #${deleteModal.row?.id} ?`}
      />
    </>
  );
};
export default SocialFeedsPage;
